// src/components/RobinsonMapViewer.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { ScatterplotLayer, GeoJsonLayer } from '@deck.gl/layers';
import { COORDINATE_SYSTEM, OrthographicView, LinearInterpolator } from '@deck.gl/core';
import { geoRobinson } from 'd3-geo-projection';

import { PIXEL_SPREAD } from '../constants/Constants.js';
import useElementSize from '../hooks/useElementSize.js';
import { getSingleColor, getMixedColor, handleSharedPointClick, getSharedTooltip, useRemoteGeoJson } from './MapSharedUtils.js';

const robinson = geoRobinson().translate([0, 0]).scale(150);
const ZOOM_OFFSET = -0.6; 

const project = (lngLat) => {
  if (!lngLat) return [0, 0];
  const [x, y] = robinson(lngLat);
  return [x, -y];
};

const unproject = (xy) => {
  if (!xy) return [0, 0];
  return robinson.invert([xy[0], -xy[1]]);
};

const projectGeometry = (geom) => {
  const transform = (coords) => (typeof coords[0] === 'number') ? project(coords) : coords.map(transform);
  return { ...geom, coordinates: transform(geom.coordinates) };
};

const RobinsonMapViewer = React.memo(function RobinsonMapViewer({ 
  id, viewState, onViewStateChange, processedData, geojsonFeatures, 
  colorBy, stats, stableColorConfig, isGradient, radiusMinPixels, radiusMaxPixels, lineWidth, onSampleClick,
  selectedSampleIndex
}) {
  const [containerRef, size] = useElementSize({ debounceMs: 50 });
  const width = size?.width || 800;
  const height = size?.height || 600;

  const [expandedId, setExpandedId] = useState(null);

  const viewStateRef = useRef(viewState);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  const prevSampleIndexRef = useRef(null);

  // Auto-Center on selected sample if it is off-screen
  useEffect(() => {
    if (selectedSampleIndex == null) {
      prevSampleIndexRef.current = null;
      return;
    }

    if (prevSampleIndexRef.current === selectedSampleIndex) {
      return;
    }

    let targetPoint = null;
    for (const group of processedData) {
      if (group.allSamples.some(s => s.index === selectedSampleIndex)) {
        targetPoint = group.position;
        break;
      }
    }

    if (targetPoint) {
      prevSampleIndexRef.current = selectedSampleIndex;

      const [lng, lat] = targetPoint;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

      const currentVS = viewStateRef.current;
      
      const targetProj = project([lng, lat]);
      const currentProj = project([currentVS.longitude, currentVS.latitude]);
      
      // Calculate OrthographicView scale factor
      const scale = Math.pow(2, currentVS.zoom + ZOOM_OFFSET);
      
      // Calculate screen boundaries based on current projected Cartesian units
      const visibleWidth = width / scale;
      const visibleHeight = height / scale;
      
      // Trigger bounds using 80% of half-width/height to leave a safe margin off the exact edges
      const safeHalfWidth = (visibleWidth / 2) * 0.8;
      const safeHalfHeight = (visibleHeight / 2) * 0.8;
      
      const dx = Math.abs(targetProj[0] - currentProj[0]);
      const dy = Math.abs(targetProj[1] - currentProj[1]);

      if (dx > safeHalfWidth || dy > safeHalfHeight) {
        onViewStateChange({
          ...currentVS,
          longitude: lng,
          latitude: lat,
          zoom: currentVS.zoom,
          transitionDuration: 800
        });
      }
    }
  }, [selectedSampleIndex, processedData, onViewStateChange, width, height]);
  
  const rawLandData = useRemoteGeoJson('https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_10m_land.geojson');

  const projectedWorldOutline = useMemo(() => {
    const ring = [];
    for (let lat = -90; lat <= 90; lat += 4) ring.push([-180, lat]);
    for (let lng = -180; lng <= 180; lng += 4) ring.push([lng, 90]);
    for (let lat = 90; lat >= -90; lat -= 4) ring.push([180, lat]);
    for (let lng = 180; lng >= -180; lng -= 4) ring.push([lng, -90]);
    ring.push(ring[0]);
    return projectGeometry({ type: 'Polygon', coordinates: [ring] });
  }, []);

  const projectedLandmass = useMemo(() => rawLandData ? { ...rawLandData, features: rawLandData.features.map(f => ({ ...f, geometry: projectGeometry(f.geometry) })) } : null, [rawLandData]);
  const projectedUserFeatures = useMemo(() => (geojsonFeatures || []).map(f => ({ ...f, geometry: projectGeometry(f.geometry) })), [geojsonFeatures]);

  const cartesianViewState = useMemo(() => ({
    target: [...project([viewState.longitude, viewState.latitude]), 0],
    zoom: viewState.zoom + ZOOM_OFFSET, 
    transitionDuration: viewState.transitionDuration,
    transitionInterpolator: viewState.transitionDuration ? new LinearInterpolator(['target']) : undefined
  }), [viewState]);

  const onViewStateChangeInternal = useCallback(({ viewState: nextViewState }) => {
    const coords = unproject(nextViewState.target);
    if (coords && !isNaN(coords[0])) onViewStateChange({ ...nextViewState, longitude: coords[0], latitude: coords[1], zoom: nextViewState.zoom - ZOOM_OFFSET });
    else onViewStateChange(nextViewState);
  }, [onViewStateChange]);

  const { binaryAttributes, lookupData } = useMemo(() => {
    const allPoints = [];
    processedData.forEach(point => {
      const pointId = point.id || `${point.position[0]}-${point.position[1]}`;
      const isExpandedCenter = expandedId === pointId && point.count > 1;
      const mixedColor = getMixedColor(point.allValues, isGradient, stats, stableColorConfig);
      const [x, y] = project(point.position);
      
      allPoints.push({ ...point, pointId, isCenter: true, mixedColor, projPos: [x, y] });

      if (isExpandedCenter) {
        const safeZoom = Math.max(2.5, Math.min(Number.isFinite(viewState.zoom) ? viewState.zoom : 0, 8));

        point.allSamples.forEach((sample, childIdx) => {
          const angle = (childIdx / point.count) * 2 * Math.PI;
          const offsetX = Math.cos(angle) * (4 * PIXEL_SPREAD / Math.pow(2, safeZoom));
          const offsetY = Math.sin(angle) * (4 * PIXEL_SPREAD / Math.pow(2, safeZoom));
          
          const childColor = getSingleColor(sample.value, isGradient, stats, stableColorConfig);
          allPoints.push({
            ...point, isChild: true, displayVal: sample.value, sampleIndex: sample.index, isCenter: false, mixedColor: childColor, projPos: [x + offsetX, y + offsetY]
          });
        });
      }
    });

    const normalIndices = [];
    const selectedIndices = [];
    
    for (let i = 0; i < allPoints.length; i++) {
      const d = allPoints[i];
      const isExpandedCenter = d.isCenter && expandedId === d.pointId && d.count > 1;
      
      let isSelected = false;
      if (selectedSampleIndex != null) {
        if (d.isChild) {
          isSelected = d.sampleIndex === selectedSampleIndex;
        } else if (!isExpandedCenter) {
          isSelected = d.allSamples.some(s => s.index === selectedSampleIndex);
        }
      }

      if (isSelected) {
        selectedIndices.push(i);
      } else {
        normalIndices.push(i);
      }
    }

    const visibleIndices = [...normalIndices, ...selectedIndices, ...selectedIndices];
    const n = visibleIndices.length;

    const pos = new Float32Array(n * 3);
    const fillColors = new Uint8Array(n * 4);
    const lineColors = new Uint8Array(n * 4);
    const radii = new Float32Array(n);
    const lw = new Float32Array(n);
    const lookup = new Array(n);

    const numNormal = normalIndices.length;
    const numSelected = selectedIndices.length;

    for (let i = 0; i < n; i++) {
      const d = allPoints[visibleIndices[i]];
      pos[i * 3] = d.projPos[0];
      pos[i * 3 + 1] = d.projPos[1];
      pos[i * 3 + 2] = 0;
      
      const isExpandedCenter = d.isCenter && expandedId === d.pointId && d.count > 1;
      const fCol = isExpandedCenter ? [255, 255, 255, 255] : d.mixedColor;
      
      fillColors.set(fCol, i * 4);
      
      if (i < numNormal) {
        const actualLineWidth = lineWidth || 0;
        if (actualLineWidth === 0) {
          lineColors.set([fCol[0], fCol[1], fCol[2], 0], i * 4);
        } else {
          lineColors.set(isExpandedCenter ? [0, 0, 0, 255] : [255, 255, 255, 200], i * 4);
        }
        lw[i] = actualLineWidth;
        radii[i] = d.isChild ? 4 : Math.sqrt(d.count || 1);
      } else if (i < numNormal + numSelected) {
        lineColors.set([255, 255, 255, 255], i * 4);
        lw[i] = Math.max(lineWidth || 0, d.isChild ? 2 : 3) + 3;
        radii[i] = (d.isChild ? 4 : Math.sqrt(d.count || 1)) + 2;
      } else {
        lineColors.set([245, 73, 39, 255], i * 4);
        lw[i] = Math.max(lineWidth || 0, d.isChild ? 2 : 3);
        radii[i] = (d.isChild ? 4 : Math.sqrt(d.count || 1)) + 2;
      }
      lookup[i] = d;
    }

    return {
      binaryAttributes: {
        instancePositions: { value: pos, size: 3 },
        instanceFillColors: { value: fillColors, size: 4, normalized: true },
        instanceLineColors: { value: lineColors, size: 4, normalized: true },
        instanceLineWidths: { value: lw, size: 1 },
        instanceRadius: { value: radii, size: 1 }
      }, lookupData: lookup
    };
  }, [processedData, expandedId, isGradient, stats, stableColorConfig, (expandedId ? viewState.zoom : null), selectedSampleIndex, lineWidth]);

  const layers = useMemo(() => [
    new GeoJsonLayer({ id: 'world-background', data: projectedWorldOutline, coordinateSystem: COORDINATE_SYSTEM.CARTESIAN, getFillColor: [183, 223, 249], stroked: false, pickable: false }),
    new GeoJsonLayer({ id: 'land-layer', data: projectedLandmass, coordinateSystem: COORDINATE_SYSTEM.CARTESIAN, getFillColor: [35, 40, 55], getLineColor: [60, 70, 90], lineWidthMinPixels: 1, lineWidthMaxPixels:1 , pickable: false }),
    new GeoJsonLayer({
      id: `geojson-${id}`, data: { type: 'FeatureCollection', features: projectedUserFeatures }, coordinateSystem: COORDINATE_SYSTEM.CARTESIAN, pickable: true, stroked: true, lineWidthMinPixels: lineWidth, lineWidthMaxPixels: lineWidth,
      getFillColor: f => getSingleColor(f.properties[colorBy], isGradient, stats, stableColorConfig),
      getLineColor: f => getSingleColor(f.properties[colorBy], isGradient, stats, stableColorConfig),
      updateTriggers: { getFillColor: [colorBy, isGradient, stats, stableColorConfig], getLineColor: [colorBy, isGradient, stats, stableColorConfig] }
    }),
    new ScatterplotLayer({
      id: `points-${id}`, data: { length: lookupData.length, attributes: binaryAttributes }, coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
      pickable: true, stroked: true, radiusUnits: 'pixels', radiusMinPixels: radiusMinPixels, radiusMaxPixels: radiusMaxPixels, 
      lineWidthUnits: 'pixels', getLineWidth: 1, lineWidthMinPixels: 0, lineWidthMaxPixels: 10,
      onClick: (info) => handleSharedPointClick(info, lookupData, onSampleClick, setExpandedId),
      updateTriggers: { attributes: [binaryAttributes] }
    })
  ], [id, projectedWorldOutline, projectedLandmass, projectedUserFeatures, lookupData.length, binaryAttributes, colorBy, isGradient, stats, stableColorConfig, lineWidth, radiusMinPixels, radiusMaxPixels]);

  return (
    <div ref={containerRef} className="w-full h-full bg-white">
      <DeckGL
        views={new OrthographicView({ id: 'ortho', flipY: false })} viewState={cartesianViewState} onViewStateChange={onViewStateChangeInternal}
        controller={true} layers={layers} getTooltip={(info) => getSharedTooltip(info, lookupData, expandedId, colorBy)}
      />
    </div>
  );
});

export default RobinsonMapViewer;